<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { remarketingDashboardAPI } from 'dashboard/api/remarketingSequences';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const loading = ref(true);
const data = ref({ totals: {}, sequences: [] });

const fetchData = async () => {
  loading.value = true;
  try {
    const res = await remarketingDashboardAPI.get();
    data.value = res.data;
  } finally {
    loading.value = false;
  }
};

const totals = computed(() => data.value.totals || {});
const sequences = computed(() => data.value.sequences || []);

const goToList = () => {
  router.push({
    name: 'remarketing_list',
    params: { accountId: route.params.accountId },
  });
};

const formatDuration = seconds => {
  if (!seconds) return '0s';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
};

onMounted(fetchData);
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="t('REMARKETING.LOADING')"
    :no-records-found="!loading && sequences.length === 0"
    :no-records-message="t('REMARKETING.DASHBOARD.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('REMARKETING.DASHBOARD.TITLE')"
        :description="t('REMARKETING.DASHBOARD.DESCRIPTION')"
        feature-name="remarketing"
      >
        <template #actions>
          <Button
            variant="outline"
            icon="i-lucide-arrow-left"
            :label="t('REMARKETING.DASHBOARD.BACK')"
            @click="goToList"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col gap-6">
        <!-- Totals strip -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div class="p-4 rounded-lg border border-n-weak bg-n-alpha-black1">
            <div class="text-xs text-n-slate-10">
              {{ t('REMARKETING.DASHBOARD.TOTALS.SEQUENCES') }}
            </div>
            <div class="text-2xl font-semibold text-n-slate-12 mt-1">
              {{ totals.sequences_count || 0 }}
              <span class="text-sm text-n-slate-10">
                ({{ totals.sequences_active || 0 }}
                {{ t('REMARKETING.DASHBOARD.TOTALS.ACTIVE_SEQUENCES') }})
              </span>
            </div>
          </div>
          <div class="p-4 rounded-lg border border-n-weak bg-n-alpha-black1">
            <div class="text-xs text-n-slate-10">
              {{ t('REMARKETING.DASHBOARD.TOTALS.ENROLLMENTS') }}
            </div>
            <div class="text-2xl font-semibold text-n-slate-12 mt-1">
              {{ totals.enrollments_total || 0 }}
            </div>
          </div>
          <div class="p-4 rounded-lg border border-n-weak bg-n-alpha-black1">
            <div class="text-xs text-n-slate-10">
              {{ t('REMARKETING.DASHBOARD.TOTALS.LIVE') }} /
              {{ t('REMARKETING.DASHBOARD.TOTALS.COMPLETED') }}
            </div>
            <div class="text-2xl font-semibold text-n-brand mt-1">
              {{ totals.enrollments_live || 0 }}
              <span class="text-base text-n-slate-11">
                / {{ totals.enrollments_completed || 0 }}
              </span>
            </div>
          </div>
          <div class="p-4 rounded-lg border border-n-weak bg-n-alpha-black1">
            <div class="text-xs text-n-slate-10">
              {{ t('REMARKETING.DASHBOARD.TOTALS.RESPONDED') }}
            </div>
            <div class="text-2xl font-semibold text-n-teal-11 mt-1">
              {{ totals.stopped_by_response || 0 }}
              <span class="text-sm text-n-slate-10">
                / {{ totals.enrollments_stopped || 0 }}
                {{ t('REMARKETING.DASHBOARD.TOTALS.STOPPED').toLowerCase() }}
              </span>
            </div>
          </div>
        </div>

        <!-- Per-sequence cards -->
        <div class="flex flex-col gap-4">
          <div
            v-for="seq in sequences"
            :key="seq.id"
            class="p-5 rounded-lg border border-n-weak bg-n-alpha-black1"
          >
            <div class="flex items-start justify-between mb-4">
              <div>
                <h3 class="text-lg font-semibold text-n-slate-12">
                  {{ seq.name }}
                </h3>
                <div class="flex items-center gap-2 mt-1 text-xs">
                  <span
                    :class="
                      seq.active
                        ? 'bg-n-teal-3 text-n-teal-11'
                        : 'bg-n-slate-3 text-n-slate-11'
                    "
                    class="px-2 py-0.5 rounded"
                  >
                    {{ seq.active ? t('REMARKETING.ACTIVE') : t('REMARKETING.INACTIVE') }}
                  </span>
                  <span class="text-n-slate-10">
                    {{ t(`REMARKETING.TRIGGER.${seq.trigger_type.toUpperCase()}`) }}
                  </span>
                </div>
              </div>
              <div class="text-right">
                <div class="text-xs text-n-slate-10">
                  {{ t('REMARKETING.DASHBOARD.SEQUENCE.RESPONSE_RATE') }}
                </div>
                <div class="text-2xl font-semibold text-n-teal-11">
                  {{ seq.response_rate }}%
                </div>
              </div>
            </div>

            <div class="grid grid-cols-4 gap-3 mb-4 text-sm">
              <div>
                <div class="text-xs text-n-slate-10">
                  {{ t('REMARKETING.DASHBOARD.TOTALS.ENROLLMENTS') }}
                </div>
                <div class="text-n-slate-12 font-medium">
                  {{ seq.enrollments.total }}
                </div>
              </div>
              <div>
                <div class="text-xs text-n-slate-10">
                  {{ t('REMARKETING.DASHBOARD.TOTALS.LIVE') }}
                </div>
                <div class="text-n-brand font-medium">
                  {{ seq.enrollments.live }}
                </div>
              </div>
              <div>
                <div class="text-xs text-n-slate-10">
                  {{ t('REMARKETING.DASHBOARD.TOTALS.COMPLETED') }}
                </div>
                <div class="text-n-slate-12 font-medium">
                  {{ seq.enrollments.completed }}
                </div>
              </div>
              <div>
                <div class="text-xs text-n-slate-10">
                  {{ t('REMARKETING.DASHBOARD.TOTALS.STOPPED') }}
                </div>
                <div class="text-n-slate-11 font-medium">
                  {{ seq.enrollments.stopped }}
                </div>
              </div>
            </div>

            <!-- Steps funnel -->
            <div v-if="seq.steps.length > 0" class="border-t border-n-weak pt-3">
              <div class="text-xs text-n-slate-10 mb-2">
                {{ t('REMARKETING.DASHBOARD.SEQUENCE.STEPS') }}
              </div>
              <div class="flex flex-col gap-2">
                <div
                  v-for="step in seq.steps"
                  :key="step.position"
                  class="grid grid-cols-12 gap-2 items-center text-xs"
                >
                  <div class="col-span-2 text-n-slate-11">
                    {{ t('REMARKETING.STEP') }} {{ step.position + 1 }}
                  </div>
                  <div class="col-span-2 text-n-slate-10">
                    {{ step.content_type }} · {{ formatDuration(step.wait_seconds) }}
                  </div>
                  <div class="col-span-3 text-n-slate-12">
                    {{ step.reached }} {{ t('REMARKETING.DASHBOARD.SEQUENCE.REACHED') }}
                  </div>
                  <div class="col-span-2 text-n-teal-11">
                    {{ step.responded_here }} {{ t('REMARKETING.DASHBOARD.SEQUENCE.RESPONDED_HERE') }}
                  </div>
                  <div class="col-span-3 text-n-slate-10">
                    {{ step.stopped_here }} {{ t('REMARKETING.DASHBOARD.SEQUENCE.STOPPED_HERE') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
